<template>
  <div v-if="value != null" class="flex items-center gap-1.5">
    <span
      v-if="minLabel"
      class="text-[10px] text-gray-400 tabular-nums shrink-0 text-right"
      :style="{ minWidth: rangeLabelMinWidth }"
    >{{ minLabel }}</span>

    <div
      class="relative h-4 bg-gray-100 rounded-sm overflow-hidden flex-1"
      :style="{ minWidth: minWidthPx + 'px' }"
    >
      <!-- Filled portion = the current value -->
      <div
        class="absolute inset-y-0 left-0 rounded-sm transition-all"
        :class="barClass"
        :style="{ width: fillWidthPct + '%' }"
      ></div>

      <!-- Tick mark = the prior-period value (e.g. last year), if provided -->
      <div
        v-if="priorTickPct != null"
        class="absolute inset-y-0 w-0.5 z-10"
        :class="tickClass"
        :style="{ left: priorTickPct + '%' }"
      ></div>

      <!-- Inline label: white-on-fill when wide enough, otherwise outside the fill -->
      <span
        v-if="label"
        class="absolute top-0 h-full flex items-center text-[11px] font-semibold tabular-nums pointer-events-none"
        :class="labelFitsInside ? labelInsideClass : labelOutsideClass"
        :style="labelStyle"
      >{{ label }}</span>
    </div>

    <span
      v-if="maxLabel"
      class="text-[10px] text-gray-400 tabular-nums shrink-0"
      :style="{ minWidth: rangeLabelMinWidth }"
    >{{ maxLabel }}</span>
  </div>
  <div v-else class="text-gray-400 text-xs text-right">—</div>
</template>

<script>
/**
 * Bullet-bar viz primitive: filled bar over a track, optional tick for a
 * comparison value, optional inline label that moves outside the fill when
 * the fill is too narrow to legibly contain it.
 *
 * Tenant-agnostic — caller supplies the pre-formatted `label` (e.g. "70.1%")
 * and the range. Used by anchor-widget table view and the legacy
 * PerformanceSummary pivot view.
 */
export default {
  name: 'BulletBar',
  props: {
    value: { type: Number, default: null },
    priorValue: { type: Number, default: null },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 1 },
    label: { type: String, default: '' },
    barClass: { type: String, default: 'bg-neutral-400' },
    tickClass: { type: String, default: 'bg-primary-500' },
    labelInsideClass: { type: String, default: 'text-white' },
    labelOutsideClass: { type: String, default: 'text-neutral-900' },
    minWidthPx: { type: Number, default: 140 },
    // Optional range endpoints rendered as small gray labels on each side of
    // the bar (e.g. "0%" / "100%"). Caller pre-formats. Omit either to hide.
    minLabel: { type: String, default: '' },
    maxLabel: { type: String, default: '' },
    rangeLabelMinWidth: { type: String, default: '1.75rem' },
  },
  computed: {
    fillWidthPct() {
      return this.pctFor(this.value);
    },
    priorTickPct() {
      return this.priorValue != null ? this.pctFor(this.priorValue) : null;
    },
    labelFitsInside() {
      return this.fillWidthPct > 35;
    },
    labelStyle() {
      const pct = this.fillWidthPct;
      if (pct > 35) {
        return {
          left: '4px',
          width: (pct - 1) + '%',
          justifyContent: 'flex-end',
          paddingRight: '4px',
        };
      }
      return { left: pct + '%', paddingLeft: '4px' };
    },
  },
  methods: {
    pctFor(v) {
      if (v == null) return 0;
      if (this.max <= this.min) return 0;
      return Math.min(Math.max(((v - this.min) / (this.max - this.min)) * 100, 0), 100);
    },
  },
};
</script>
